/**
 * enterCommand — starts a new G/R/S-style command.
 *
 * Responsibilities:
 * - Validate that there's something to operate on (selection, cursor, bone).
 * - Capture an undoable snapshot (or reuse the one the caller provided).
 * - Pre-compute per-command-type enter data:
 *     • edgeSlide: slide rails from each selected vertex's neighbors
 *     • bone commands: FK orig pose / IK chain state / body-grab pins
 *     • proportional edit: per-vertex falloff weights
 * - Resolve a pending keyboard axis through the current transform orientation.
 * - Build the `commandBuffer` and update interaction mode.
 *
 * Pure function — takes `set`/`get` explicitly rather than closing over a
 * factory, so the slice is straightforward to compose in index.ts.
 */

import * as THREE from "three/webgpu";
import { recordCommandEnter } from "../../debug/listenerProbe.ts";
import {
  getBoneMap,
  getBone as getBoneRef,
} from "../../mannequin/boneRegistry.ts";
import { captureChainState } from "../../mannequin/ikChainState.ts";
import { findChainForBone } from "../../mannequin/ikChains.ts";
import { createIKSession } from "../../mannequin/ikStabilizer.ts";
import { createWorkerSession } from "../../mannequin/ikWorkerClient.ts";
import {
  getDefaultParams,
  usesTopologyPreview,
} from "../../modeling/operatorParams.ts";
import { computePivot, usesIndividualOrigins } from "../../modeling/pivot.ts";
import { computeProportionalWeights } from "../../modeling/proportional.ts";
import { getAdjacencyCache, vertexNeighbors } from "../../modeling/topology.ts";
import { screenToWorldRadius } from "../../viewport/screenRadius.ts";
import {
  getCollectionForObject,
  getSceneObjectWorldTransform,
} from "../collectionGraph.ts";
import { clearLiveEditPreview } from "../liveEditPreview.ts";
import {
  findDependentRebuildTargets,
  type GeneratorTarget,
} from "../storeGeneratorOps.ts";
import type {
  AppState,
  AxisConstraint,
  CommandSnapshot,
  CommandType,
  EdgeSlideRail,
  InteractionMode,
} from "../storeTypes.ts";
import {
  captureEditObjectSnapshot,
  captureSnapshot,
  commandSnapshotObject,
  isFullSnapshot,
  undoManager,
} from "../storeUndo.ts";
import { resolveKeyboardAxis } from "./commandAxis.ts";
import type { Get_, Set_ } from "./core.ts";
import {
  buildEditTransformComponents,
  buildProportionalPivotLookup,
  collectSnapshotTransformTargets,
  cursorInObjectLocal,
  type EditTransformComponent,
} from "./transformMath.ts";

/**
 * Compute the cached editPivot and snapshot-ordered selected-vertex list
 * from a pre-captured snapshot. Exported so the small number of inline
 * commandBuffer constructions outside enterCommand (topology-op entries in
 * storeEditOps/meshOps.ts) populate the cache on the same terms as the
 * main enterCommand path. The live-apply layer treats editPivot as
 * mandatory for every pivot-consuming command type — no fallback — so
 * every commandBuffer construction that targets one of those types must
 * compute it here.
 */
export function computeEditPivotCache(
  state: AppState,
  em: { objectId: string; selectedVertices: Set<number> },
  snap: CommandSnapshot,
): {
  editPivot: [number, number, number];
  editSelectionVertices: number[];
} | null {
  const snapObj = commandSnapshotObject(snap, em.objectId);
  if (!snapObj) return null;
  const editSelectionVertices = Array.from(em.selectedVertices);
  const pivotPositions: [number, number, number][] = new Array(
    editSelectionVertices.length,
  );
  for (let i = 0; i < editSelectionVertices.length; i++) {
    const vi = editSelectionVertices[i]!;
    pivotPositions[i] = [
      snapObj.vertices[vi * 3],
      snapObj.vertices[vi * 3 + 1],
      snapObj.vertices[vi * 3 + 2],
    ];
  }
  const editPivot = computePivot(
    pivotPositions,
    state.pivotReference,
    cursorInObjectLocal(
      snapObj,
      getCollectionForObject(state.collections, em.objectId),
      state.cursor3D.position,
    ),
  );
  return { editPivot, editSelectionVertices };
}

export interface EnterCommandOpts {
  axis?: AxisConstraint;
  normalVector?: [number, number, number];
  boneLocalAxis?: "x" | "y" | "z";
  preEditSelection?: Set<number>;
  preSnapshot?: CommandSnapshot;
  cancelSnapshot?: CommandSnapshot;
  gizmoInitiated?: boolean;
  bodyGrab?: boolean;
}

export function enterCommand(
  set: Set_,
  get: Get_,
  type: CommandType,
  opts?: EnterCommandOpts,
): void {
  const state = get();
  clearLiveEditPreview();

  // Block object-level transforms in sculpt mode
  if (state.sculptMode != null) return;

  const inEditMode = state.mode === "edit" && state.editMode != null;
  const inPoseMode = state.poseMode != null;
  const isBoneCommand = type === "boneRotate" || type === "boneTranslate";
  const isCursorCommand = type === "cursorTranslate";
  const hasSelection = isCursorCommand
    ? state.cursor3D.selected
    : isBoneCommand
      ? (state.poseMode?.selectedBones?.size ?? 0) > 0
      : inEditMode
        ? state.editMode!.selectedVertices.size > 0
        : state.selectedObjectTargets.length > 0;
  if (!hasSelection) return;

  // Use provided snapshot or capture a new one. Edit-mode geometry commands
  // use an object-local baseline; object/pose/cursor commands keep the full
  // snapshot path because their changes can touch broad scene state.
  const usesEditObjectSnapshot = inEditMode && !isCursorCommand;
  const snap =
    opts?.preSnapshot ??
    (usesEditObjectSnapshot
      ? captureEditObjectSnapshot(state, state.editMode!.objectId)
      : captureSnapshot(state));
  if (!snap) return;
  const shouldPushUndo = !opts?.preSnapshot && isFullSnapshot(snap);

  // Pre-compute edge slide rails when entering edge slide mode
  let edgeSlideRails: Map<number, EdgeSlideRail> | null = null;
  if (type === "edgeSlide" && inEditMode) {
    const em = state.editMode!;
    const obj = state.sceneObjects.get(em.objectId);
    if (obj?.topology) {
      const slideCache = getAdjacencyCache(
        obj.topology,
        obj.vertices.length / 3,
      );
      const verts = obj.vertices;
      const selected = em.selectedVertices;
      edgeSlideRails = new Map();

      // For each selected vertex, compute slide rail from its non-selected neighbors
      for (const vi of selected) {
        const origin: [number, number, number] = [
          verts[vi * 3],
          verts[vi * 3 + 1],
          verts[vi * 3 + 2],
        ];

        // Collect non-selected neighbor positions
        const neighborPositions: [number, number, number][] = [];
        const neighbors = vertexNeighbors(slideCache, obj.topology, vi);
        for (const n of neighbors) {
          if (selected.has(n)) continue;
          neighborPositions.push([
            verts[n * 3],
            verts[n * 3 + 1],
            verts[n * 3 + 2],
          ]);
        }

        if (neighborPositions.length === 0) continue;

        let a: [number, number, number];
        let b: [number, number, number] | null;

        if (neighborPositions.length === 1) {
          a = neighborPositions[0];
          b = null;
        } else if (neighborPositions.length === 2) {
          a = neighborPositions[0];
          b = neighborPositions[1];
        } else {
          // 3+ neighbors: pick the pair most collinear through V (most negative dot)
          let bestI = 0,
            bestJ = 1,
            bestDot = Infinity;
          for (let i = 0; i < neighborPositions.length; i++) {
            for (let j = i + 1; j < neighborPositions.length; j++) {
              const ni = neighborPositions[i],
                nj = neighborPositions[j];
              const dx1 = ni[0] - origin[0],
                dy1 = ni[1] - origin[1],
                dz1 = ni[2] - origin[2];
              const dx2 = nj[0] - origin[0],
                dy2 = nj[1] - origin[1],
                dz2 = nj[2] - origin[2];
              const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1 + dz1 * dz1);
              const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2 + dz2 * dz2);
              if (len1 < 1e-10 || len2 < 1e-10) continue;
              const dot = (dx1 * dx2 + dy1 * dy2 + dz1 * dz2) / (len1 * len2);
              if (dot < bestDot) {
                bestDot = dot;
                bestI = i;
                bestJ = j;
              }
            }
          }
          a = neighborPositions[bestI];
          b = neighborPositions[bestJ];
        }

        edgeSlideRails.set(vi, { origin, a, b });
      }

      if (edgeSlideRails.size === 0) return;
    } else {
      return; // No topology — can't edge slide
    }
  }

  // For bone commands, capture the bone's current local quaternion and parent world quaternion.
  // These are read from the live THREE.Bone objects (which have current poses applied).
  let boneCommandData: import("../storeTypes.ts").CommandBuffer["boneCommandData"] =
    null;
  if (isBoneCommand && state.poseMode?.objectId && state.poseMode?.activeBone) {
    const bone = getBoneRef(state.poseMode.objectId, state.poseMode.activeBone);
    if (bone) {
      const parentWorldQuat = new THREE.Quaternion();
      if (bone.parent) bone.parent.getWorldQuaternion(parentWorldQuat);
      boneCommandData = {
        origLocalQuat: [
          bone.quaternion.x,
          bone.quaternion.y,
          bone.quaternion.z,
          bone.quaternion.w,
        ],
        parentWorldQuat: [
          parentWorldQuat.x,
          parentWorldQuat.y,
          parentWorldQuat.z,
          parentWorldQuat.w,
        ],
      };

      // For IK (boneTranslate), capture the chain state
      if (type === "boneTranslate") {
        const boneMapObj = getBoneMap(state.poseMode.objectId);
        const chainDef = findChainForBone(state.poseMode.activeBone);
        if (boneMapObj && chainDef && chainDef.chain.length >= 2) {
          const chainState = captureChainState(boneMapObj, chainDef.chain);
          if (chainState) {
            // Deep copy original positions for reference
            const origPositions = chainState.positions.map((p) => p.clone());
            boneCommandData.ikChainState = chainState;
            boneCommandData.ikOrigPositions = origPositions;
            boneCommandData.ikPoleDirection = chainDef.poleDirection;
            boneCommandData.ikSolverType = chainDef.solverType;
            boneCommandData.ikJointWeights = chainDef.jointWeights;

            // Initialize stabilization session for continuous IK dragging
            createIKSession();
            createWorkerSession();
          }
        }

        // Body-grab mode: capture extremity world positions as auto-pins.
        // These are merged with user pins at solve time so the whole-body
        // IK solver keeps hands/feet planted while the hips move.
        if (opts?.bodyGrab) {
          const bodyPinNames = ["handL", "handR", "footL", "footR"];
          const pins = new Map<string, [number, number, number]>();
          const bm = getBoneMap(state.poseMode.objectId);
          if (bm) {
            for (const name of bodyPinNames) {
              const b = bm.get(name);
              if (b) {
                const p = new THREE.Vector3();
                b.getWorldPosition(p);
                pins.set(name, [p.x, p.y, p.z]);
              }
            }
          }
          if (pins.size > 0) {
            boneCommandData.bodyGrabPins = pins;
          }
        }
      }
    }
  }

  // Pre-compute proportional editing weights for edit-mode G/R/S.
  // The stored radius is in screen-space CSS pixels — convert to
  // object-local units using the cached camera state and object transform.
  let proportionalWeights: Map<number, number> | null = null;
  if (
    state.proportionalEnabled &&
    inEditMode &&
    (type === "translate" ||
      type === "rotate" ||
      type === "scale" ||
      type === "shrinkFatten" ||
      type === "skinRadius")
  ) {
    const obj = state.sceneObjects.get(state.editMode!.objectId);
    if (obj) {
      // Approximate world-space position of the editing context from the
      // object's world transform (collection.transform ∘ obj local) so the
      // screen-space radius converts at the rendered depth, not the child's
      // collection-local position.
      const worldTransform = getSceneObjectWorldTransform(
        state.sceneObjects,
        state.collections,
        state.editMode!.objectId,
      );
      const refPos = worldTransform?.position ?? obj.position;
      const refScale = worldTransform?.scale ?? obj.scale;
      const worldRef = new THREE.Vector3(refPos[0], refPos[1], refPos[2]);
      const worldRadius = screenToWorldRadius(
        state.proportionalRadius,
        worldRef,
      );
      const avgScale = (refScale[0] + refScale[1] + refScale[2]) / 3;
      const localRadius =
        avgScale > 1e-8 ? worldRadius / avgScale : worldRadius;

      proportionalWeights = computeProportionalWeights(
        obj.vertices,
        state.editMode!.selectedVertices,
        localRadius,
        state.proportionalFalloff,
      );
    }
  }

  // Pre-compute the edit-mode caches that live-apply consumes every frame:
  // the pivot, the snapshot-ordered selection list, and (when individual
  // origins is active) per-component pivots + proportional nearest-pivot
  // lookup. All are command-stable — selection and topology don't change
  // mid-drag, pivots come from the snapshot's vertex positions — so they
  // are built once here rather than rebuilt every pointermove.
  //
  // editPivot is populated for every edit-mode command type that consumes
  // px/py/pz in applyLiveEditCommand (the basic transforms plus every
  // topology op). edgeSlide does not read the pivot, so its slot stays
  // null by design. After this block the live-apply path asserts the
  // cache is present for consumers — no per-frame fallback.
  let individualOriginsComponents: EditTransformComponent[] | null = null;
  let individualOriginsProportionalPivots: Map<
    number,
    [number, number, number]
  > | null = null;
  let editPivot: [number, number, number] | null = null;
  let editSelectionVertices: number[] | null = null;
  const pivotConsumer =
    type === "translate" ||
    type === "rotate" ||
    type === "scale" ||
    type === "shrinkFatten" ||
    type === "skinRadius" ||
    usesTopologyPreview(type);
  if (inEditMode && pivotConsumer) {
    const em = state.editMode!;
    // editMode without a matching snapshot entry means the edited object
    // vanished between render and command-enter — a contradiction under
    // synchronous store updates. Bail: the command can't run without it.
    const pivotCache = computeEditPivotCache(state, em, snap);
    if (!pivotCache) return;
    editPivot = pivotCache.editPivot;
    editSelectionVertices = pivotCache.editSelectionVertices;
    if (
      usesIndividualOrigins(state.pivotReference) &&
      (type === "translate" || type === "rotate" || type === "scale")
    ) {
      const snapObj = commandSnapshotObject(snap, em.objectId)!;
      individualOriginsComponents = buildEditTransformComponents(
        snapObj.vertices,
        snapObj.topology ?? null,
        em,
      );
      if (proportionalWeights && individualOriginsComponents.length > 0) {
        individualOriginsProportionalPivots = buildProportionalPivotLookup(
          snapObj.vertices,
          individualOriginsComponents,
          proportionalWeights,
        );
      }
    }
  }

  // Pre-resolve the dependent-generator owners that need rebuilding when
  // any of the moved object/collection sources change. The dependency
  // graph is command-stable for the duration of the drag, so caching
  // this once at command-enter avoids the per-frame O(generators) scan
  // that previously ran for every moved target on every pointermove.
  // Collection targets expand to their member object ids — same shape
  // as the per-frame walk in `applyObjectTransformPatch`.
  let dependentRebuildTargets: GeneratorTarget[] | null = null;
  if (
    !inEditMode &&
    !inPoseMode &&
    !isCursorCommand &&
    (type === "translate" || type === "rotate" || type === "scale") &&
    state.selectedObjectTargets.length > 0 &&
    isFullSnapshot(snap)
  ) {
    const snapTargets = collectSnapshotTransformTargets(
      snap,
      state.selectedObjectTargets,
    );
    const sourceIds = new Set<string>();
    for (const target of snapTargets) {
      if (target.kind === "scene") {
        sourceIds.add(target.id);
      } else if (target.kind === "collection") {
        const collection = state.collections.get(target.id);
        if (collection) {
          for (const objectId of collection.objectIds) sourceIds.add(objectId);
        }
      }
    }
    dependentRebuildTargets = findDependentRebuildTargets(state, sourceIds);
  }

  // If the caller provided an axis+normalVector (gizmo path), use as-is.
  // Otherwise resolve pendingAxis through transform orientation.
  const resolvedPending =
    !opts?.axis && state.pendingAxis
      ? resolveKeyboardAxis(state, state.pendingAxis)
      : null;
  const preservesTransformMode =
    usesTopologyPreview(type) ||
    type === "edgeSlide" ||
    type === "shrinkFatten" ||
    type === "skinRadius" ||
    isBoneCommand ||
    isCursorCommand;

  const patch: Partial<AppState> = {
    commandBuffer: {
      type,
      axis: opts?.axis ?? resolvedPending?.axis ?? state.pendingAxis ?? null,
      numericInput: "",
      mouseOrigin: null,
      normalVector: opts?.normalVector ?? resolvedPending?.normalVector ?? null,
      boneLocalAxis: opts?.boneLocalAxis ?? null,
      preEditSelection: opts?.preEditSelection ?? null,
      preEditEdges:
        inEditMode && state.editMode
          ? new Set(state.editMode.selectedEdges)
          : null,
      preEditFaces:
        inEditMode && state.editMode
          ? new Set(state.editMode.selectedFaces)
          : null,
      preEditActiveVertex:
        inEditMode && state.editMode ? state.editMode.lastActiveVertex : null,
      preEditActiveEdge:
        inEditMode && state.editMode ? state.editMode.lastActiveEdge : null,
      preEditActiveFace:
        inEditMode && state.editMode ? state.editMode.lastActiveFace : null,
      edgeSlideRails,
      gizmoInitiated: opts?.gizmoInitiated ?? false,
      edgeLoopInsertEdge: null,
      edgeLoopHoverEdge: null,
      edgeLoopPreview: null,
      boneCommandData,
      proportionalWeights,
      editPivot,
      editSelectionVertices,
      preEditSelectionIdentity:
        inEditMode && state.editMode ? state.editMode.selectedVertices : null,
      params: getDefaultParams(type),
      activeValueMode: null,
      individualOriginsComponents,
      individualOriginsProportionalPivots,
      dependentRebuildTargets,
      topologyPreviewSeed: null,
      topologyPreviewLastAppliedAt: 0,
    },
    commandPreSnapshot: snap,
    commandCancelSnapshot: opts?.cancelSnapshot ?? null,
    activeEditSession: null,
    transformMode: preservesTransformMode
      ? state.transformMode
      : (type as "translate" | "rotate" | "scale"),
    pendingAxis: null,
    lastOperation: null, // Clear redo panel when new command starts
    canUndo: undoManager.canUndo,
    canRedo: undoManager.canRedo,
  };
  if (!inEditMode && !inPoseMode) {
    patch.mode = "transform" as InteractionMode;
  }
  if (shouldPushUndo) undoManager.push(snap);
  patch.canUndo = undoManager.canUndo;
  patch.canRedo = undoManager.canRedo;
  recordCommandEnter();
  set(patch);
}
